/**
 * useConversationSelection Hook
 *
 * Handles bulk selection of conversations for batch operations.
 */

import {useState, useCallback, useMemo} from 'react'
import {ConversationData} from '../types/inbox-v3.types'

interface UseConversationSelectionReturn {
  // State
  selectedIds: string[]
  selectedCount: number
  isSelectionActive: boolean

  // Checks
  isSelected: (id: string) => boolean
  areAllSelected: (conversations: ConversationData[]) => boolean
  areSomeSelected: (conversations: ConversationData[]) => boolean

  // Actions
  select: (id: string) => void
  deselect: (id: string) => void
  toggle: (id: string) => void
  selectAll: (conversations: ConversationData[]) => void
  deselectAll: () => void
  selectMultiple: (ids: string[]) => void
}

export function useConversationSelection(): UseConversationSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Computed values
  const selectedCount = selectedIds.length
  const isSelectionActive = selectedCount > 0

  // Check if a specific conversation is selected
  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds])

  // Check if all conversations are selected
  const areAllSelected = useCallback(
    (conversations: ConversationData[]) => {
      if (conversations.length === 0) return false
      return conversations.every((c) => selectedIds.includes(c._id))
    },
    [selectedIds]
  )

  // Check if some (but not all) conversations are selected
  const areSomeSelected = useCallback(
    (conversations: ConversationData[]) => {
      if (conversations.length === 0) return false
      const allSelected = conversations.every((c) => selectedIds.includes(c._id))
      const someSelected = conversations.some((c) => selectedIds.includes(c._id))
      return someSelected && !allSelected
    },
    [selectedIds]
  )

  // Select a single conversation
  const select = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev
      return [...prev, id]
    })
  }, [])

  // Deselect a single conversation
  const deselect = useCallback((id: string) => {
    setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id))
  }, [])

  // Toggle selection of a single conversation
  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id)
      }
      return [...prev, id]
    })
  }, [])

  // Select all conversations
  const selectAll = useCallback((conversations: ConversationData[]) => {
    setSelectedIds(conversations.map((c) => c._id))
  }, [])

  // Deselect all conversations
  const deselectAll = useCallback(() => {
    setSelectedIds([])
  }, [])

  // Select multiple specific conversations
  const selectMultiple = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const newIds = ids.filter((id) => !prev.includes(id))
      return [...prev, ...newIds]
    })
  }, [])

  return {
    // State
    selectedIds,
    selectedCount,
    isSelectionActive,

    // Checks
    isSelected,
    areAllSelected,
    areSomeSelected,

    // Actions
    select,
    deselect,
    toggle,
    selectAll,
    deselectAll,
    selectMultiple,
  }
}